<script>
	import { onMount } from "svelte";
	import { scaleLinear } from "d3-scale";
	import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
	import centerOfMass from "@turf/center-of-mass";

	import Search from "./components/Search/Search.svelte";
	import MediaQuery from "./components/MediaQuerySsr.svelte";
	import Map from "./map/map";
	import Tooltip from "./Tooltip.svelte";

	import { loadJSON, loadMapbox, loadTopojson, scrollTo, convertToGeoJSON, getStripes, deepcopy } from "./utils";

	import {
		bezirke,
		markerData,
		geoData,
		options,
		optionsById,
		parties,
		initialBounds,
		initialBoundsTablet,
		maxBounds,
		baseOpacity,
		baseOpacityLuftbilder,
	} from "./constants";

	// ///////////////////////////////////////////////////////////
	// props
	// ///////////////////////////////////////////////////////////
	export let name;

	// ///////////////////////////////////////////////////////////
	// constants and definitions
	// ///////////////////////////////////////////////////////////
	let map;
	let tooltip;
	let stopSearch;

	let mapZoom;

	let mapActivated = false;
	let mapLoaded = false;

	let labelsBezirke = [];
	let labelsOrtsteile = [];

	let choroplethOpacity = baseOpacity;
	$: choroplethOpacity = showLuftbilder ? baseOpacityLuftbilder : baseOpacity;

	let colors = [];
	let highlightingColor = "#000";

	let showLuftbilder = false;
	const luftbilderId = "luftbilder";
	const luftbilderButtonZoom = 11;

	// ///////////////////////////////////////////////////////////
	// data
	// ///////////////////////////////////////////////////////////

	let geoBezirke;
	let geoOrtsteile;
	let geoRing;

	let geoBriefwahlbezirke;
	let topoBriefwahlbezirke;

	let dataBerlin;
	let dataBerlinAgh;

	let dataResults;
	let dataResultsAgh;

	let maxResultsAgh;
	let minResults;
	let maxResults;

	// ///////////////////////////////////////////////////////////
	// ui bound parameters
	// ///////////////////////////////////////////////////////////
	export let elemMap;

	let selectedRegion;
	let selectedPoint;
	let hoveredRegion;

	$: if (selectedRegion) console.log(selectedRegion.id, selectedRegion);

	// ///////////////////////////////////////////////////////////
	// show/hide luftbilder
	// ///////////////////////////////////////////////////////////
	$: highlightingColor = showLuftbilder ? "#fff" : "#000";

	$: if (mapActivated) {
		if (showLuftbilder) {
			map.showLayer(luftbilderId);
		} else {
			map.hideLayer(luftbilderId);
		}
		if (map.map.getLayer(geoData.layerId)) map.map.setPaintProperty(geoData.layerId, "fill-opacity", choroplethOpacity);

		const idHover = `${geoData.layerId}-hovering`;
		const idSelected = `${geoData.layerId}-selected`;

		if (map.map.getLayer(idHover)) map.map.setPaintProperty(idHover, "line-color", highlightingColor);
		if (map.map.getLayer(idSelected)) map.map.setPaintProperty(idSelected, "line-color", highlightingColor);

		map.updateChoroplethOpts(geoData.layerId, {
			hoveringBorderColor: highlightingColor,
			selectedBorderColor: highlightingColor,
		});
	}

	// ///////////////////////////////////////////////////////////
	// data processing
	// ///////////////////////////////////////////////////////////
	const processData = () => {
		geoData.labels = [];

		console.log(dataResults);
		console.log(geoData.data.features);

		geoData.data.features.forEach((f, i) => {
			const id = f.properties.id;
			const data = dataResults[id];
			// const dataAgh = dataResultsAgh[id];

			// parties.forEach((p) => {
			// 	data[p.id] = dataAgh[p.id];
			// });

			data.ungueltig = 1 - data.ja - data.nein;
			data.wahlbeteiligung = data._wahlbeteiligung;
			delete data._wahlbeteiligung;

			data.winner = data.ja > data.nein ? "ja" : "nein";

			// copy the wahlbezirke and filter out the ones that are not only numbers
			const stimmbezirke = deepcopy(f.properties.wahlbezirke.filter((w) => /^\d+$/.test(w)));
			const last = stimmbezirke.pop();

			if (stimmbezirke.length > 0) {
				f.properties.name = f.properties.wahlbezirke.join(", ");
				f.properties.nameLong = `${stimmbezirke.join(", ")} und ${last}`;
				f.properties.regionLabel = `${geoData.regionLabel}e`;
				f.properties.label = `${f.properties.regionLabel} ${f.properties.nameLong}`;
				f.properties.isPlural = true;
			} else {
				f.properties.name = last;
				f.properties.nameLong = last;
				f.properties.regionLabel = `${geoData.regionLabel}`;
				f.properties.label = `${f.properties.regionLabel} ${f.properties.nameLong}`;
				f.properties.isPlural = false;
			}

			f.properties = { ...f.properties, ...data };

			f.properties.filtered = false;

			const center = centerOfMass(f).geometry.coordinates;
			f.properties.centerLng = center[0];
			f.properties.centerLat = center[1];

			f.properties.bezirk = bezirke[f.properties["BEZ"]];

			geoData.labels.push({
				name: `${f.properties.name}`,
				lnglat: [f.properties.centerLng, f.properties.centerLat],
			});
		});

		minResults = Math.min(...Object.values(dataResults).map((d) => (d.winner === "ja" ? d.ja : d.nein)));
		maxResults = Math.max(...Object.values(dataResults).map((d) => Math.max(d.ja, d.nein)));
		// maxResultsAgh = Math.max(...Object.values(dataResultsAgh).map((d) => Math.max(...parties.map((p) => d[p.id]))));

		geoBezirke.features.forEach((f) => {
			const center = centerOfMass(f).geometry.coordinates;
			f.properties.centerLng = center[0];
			f.properties.centerLat = center[1];

			f.properties.name = bezirke[f.properties["BEZ"]];

			labelsBezirke.push({
				name: `${f.properties.name}`,
				lnglat: [f.properties.centerLng, f.properties.centerLat],
			});
		});

		geoOrtsteile.features.forEach((f) => {
			labelsOrtsteile.push({
				name: `${f.properties.name}`,
				lnglat: f.geometry.coordinates,
			});
		});

		dataBerlin.wahlbeteiligung /= 100;
		dataBerlin.ja = dataBerlin.stimmen_parteien.Ja_rel;
		dataBerlin.nein = dataBerlin.stimmen_parteien.Nein_rel;
		dataBerlin.ungueltig = dataBerlin.stimmen_ungueltig_rel;
		dataBerlin.regionLabel = "Berlin";
		dataBerlin.nameLong = "Gesamtergebnis";

		// parties.forEach((p) => {
		// 	dataBerlin[p.id] = dataBerlinAgh.zweitstimmen_parteien[p.id] / dataBerlinAgh.zweitstimmen_gueltig;
		// });
	};

	// ///////////////////////////////////////////////////////////
	// set up colors
	// ///////////////////////////////////////////////////////////
	const createColors = () => {
		options.forEach((o) => {
			o.scale = scaleLinear()
				.domain([minResults, minResults + 0.5 * (maxResults - minResults), 0.9])
				.range([o.colorLight, o.color, o.colorDark]);
		});

		colors = geoData.data.features.reduce(
			(o, f) => ({
				...o,
				[f.properties[geoData.idKey]]: optionsById[f.properties.winner].scale(f.properties[f.properties.winner]),
			}),
			{}
		);
	};

	// ///////////////////////////////////////////////////////////
	// remove label for hovered/selected region
	// ///////////////////////////////////////////////////////////
	$: if (mapActivated) {
		geoData.labels.forEach((l) => {
			l.opacity =
				(hoveredRegion && l.name === hoveredRegion.name) || (selectedRegion && l.name === selectedRegion.name) ? 0 : 1;
		});

		if (map.map.getLayer(geoData.labelLayerID)) map.updateLabels(labelsBriefwahlbezirke, geoData.labelLayerID);
	}
	// ///////////////////////////////////////////////////////////
	// initialize map
	// ///////////////////////////////////////////////////////////

	let tooltipHovered = false;

	const choroplethOpts = {
		geoType: "geojson",
		borderColor: "#FFF",
		borderWidth: 0.3,
		borderOpacity: 0.5,
		opacity: choroplethOpacity,
		dataType: "colors",
		colors: colors,
		data: [0],
		interactive: true,
		zoomOnClick: true,
		zoomLevel: 12.7,
		hoveringBorderColor: highlightingColor,
		hoveringBorderWidth: 3,
		selectedBorderColor: highlightingColor,
		selectedBorderWidth: 3,
		filteredColor: "transparent",
		filteredBorderColor: "transparent",
		choroplethClickCallback: (f, filtered, lngLat) => {
			if (!filtered) selectPoint(lngLat, f);
		},
		backgroundClickCallback: () => {
			clearPoint();
		},
		mousemoveCallback: (f, filtered) => {
			if (isTouch) return;
			hoveredRegion = f.properties;
			addPopup(f);
		},
		mouseleaveCallback: () => {
			setTimeout(() => {
				if (!tooltipHovered) {
					hoveredRegion = undefined;
					removePopup();
					if (selectedRegion) addPopup(selectedRegion);
				}
			});
		},
	};

	let isTouch = false;

	onMount(async () => {
		const promises = [
			loadJSON(`${PATH}assets/data/berlin_bezirke.geo.json`),
			loadJSON(`${PATH}assets/data/ring.geo.json`),
			loadJSON(`${PATH}assets/data/all_sim.json`),
			// loadJSON(`${PATH}assets/data/all_sim_agh.json`),
			// loadJSON(`${url}ergebnisse/agh/2023/briefwahlbezirke/zweit/all_sim.json`),
			// loadJSON(`${PATH}assets/data/briefwahlbezirke.topo.json`),
			loadJSON(`${PATH}assets/data/briefwahlbezirke.geo.json`),
			// loadJSON(`${url}geo/aghbvv/2023/briefwahlbezirke.topo.json`),
			loadJSON(`${PATH}assets/data/ortsteile_labels.geo.json`),
			loadJSON(`${PATH}assets/data/berlin.json`),
			// loadJSON(`${PATH}assets/data/berlin_agh.json`),
			loadTopojson(),
			loadMapbox(),
		];

		function checkTouch() {
			isTouch = true;
			document.querySelector("body").removeEventListener("touchstart", checkTouch);
		}
		document.querySelector("body").addEventListener("touchstart", checkTouch);

		[geoBezirke, geoRing, dataResults, topoBriefwahlbezirke, geoOrtsteile, dataBerlin] = await Promise.all(promises);

		// geoBriefwahlbezirke = convertToGeoJSON(topoBriefwahlbezirke);
		geoBriefwahlbezirke = topoBriefwahlbezirke;
		geoData.data = geoBriefwahlbezirke;

		processData();

		createColors();

		map = new Map(elemMap, {
			useFranklin: true,
			initialBounds: platform === "tablet" ? initialBoundsTablet : initialBounds,
			maxBounds,
			controls: true,
			controlsLeft: true,
			scale: true,
			popup: false,
			popupShift: false,
			labels: true,
			// style: "wahl",
			styleJson: `${PATH}assets/style.json`,
			hideBackgroundMap: false,
		});

		map.activated
			.then(() => map.loaded)
			.then(() => {
				const REFLAYER = map.firstLabelLayer;

				map.hideAttribution();

				mapZoom = map.map.getZoom();
				map.map.on("zoom", () => {
					mapZoom = map.map.getZoom();
				});

				map.map.loadImage(getStripes("#ccc", 4, "#fff"), (error, image) => {
					map.map.addImage("stripes-nodata", image);
				});

				// luftbilder
				map.map.addSource(luftbilderId, {
					type: "raster",
					url: "https://interaktiv.tagesspiegel.de/maptiles/luftbilder_2022/tiles.json",
				});

				map.map.addLayer(
					{
						id: luftbilderId,
						source: luftbilderId,
						type: "raster",
					},
					REFLAYER
				);

				map.addChoropleth(geoData.data, {
					...choroplethOpts,
					id: geoData.layerId,
					identifier: geoData.idKey,
					colors: colors,
					noDataColor: "#fff",
					noDataPattern: "stripes-nodata",
					beforeLayer: REFLAYER,
					zoomPadding: () => {
						return {
							top: 0,
							left: 0,
							bottom: platform === "mobile" ? 150 : 0,
							right: platform === "mobile" ? 0 : 290,
						};
					},
				});

				// bezirke
				map.addChoropleth(geoBezirke, {
					geoType: "geojson",
					id: "bezirke",
					borderColor: "#FFF",
					borderWidth: 2,
					interactive: false,
					opacity: 0,
					beforeLayer: REFLAYER,
				});

				// labels
				const labelsOpts = {
					minZoom: 12.5,
					maxZoom: 14.5,
					fontSize: 12,
					color: "#fff",
					haloWidth: 1,
					haloColor: "#000",
					letterSpacing: 0.015,
					font: "Franklin Gothic Medium",
				};

				if (geoData.showLabels) {
					map.addLabels(geoData.labels, {
						...labelsOpts,
						id: geoData.labelLayerId,
						textAllowOverlap: true,
						textAnchor: "top",
					});
				}

				map.addLabels(labelsBezirke, {
					...labelsOpts,
					id: "labels-bezirke",
					minZoom: 8,
					maxZoom: 10.6,
				});

				map.addLabels(labelsOrtsteile, {
					...labelsOpts,
					id: "labels-ortsteile",
					minZoom: 10.6,
					maxZoom: 14,
				});

				showBerlinMarkers();
				showRing();

				mapActivated = true;

				requestAnimationFrame(() => {
					// move water in front of choropleth
					// map.map.moveLayer("water", "bezirke");
				});

				map.map.once("idle", () => {
					mapLoaded = true;

					// preselect a point
					// selectPoint(initialSelectedPoint, undefined, true);
				});
			});
	});

	// ///////////////////////////////////////////////////////////
	// map selection
	// ///////////////////////////////////////////////////////////
	let marker = null;
	let popup = null;

	const removeMarker = () => {
		if (marker) marker.remove();
	};

	const addMarker = (lon, lat) => {
		if (marker) marker.remove();
		marker = new window.mapboxgl.Marker({
			// anchor: 'bottom',
			color: "#1f1f1f",
			scale: 0.7,
		})
			.setLngLat([lon, lat])
			.addTo(map.map);
	};

	const removePopup = () => {
		if (popup) popup.remove();
	};

	const addPopup = (feature) => {
		if (!geoData.showSmallTooltips) return;
		const props = feature.properties ? feature.properties : feature;

		if (popup) popup.remove();

		const html = `
				<div class="region">${props.regionLabel}</div>
        <div class="name">${props.nameLong}</div>
        <div class="bezirk">in ${props.bezirk}</div>
      `;

		popup = new window.mapboxgl.Popup({
			color: "#1f1f1f",
			scale: 0.7,
			closeButton: false,
			closeOnClick: false,
			closeOnMove: false,
			className: "region-popup",
		})
			.setLngLat([props.centerLng, props.centerLat])
			.setHTML(html)
			.addTo(map.map);
	};

	const findFeature = (lon, lat) => {
		let feature = null;
		// first try mapbox, only works if feature is visible
		// then search in the geometry data
		const point = map.map.project([lon, lat]);

		const fs = map.map.queryRenderedFeatures(point, {
			layers: [geoData.layerId],
		});

		if (fs.length === 1) {
			feature = fs[0];
		} else {
			const pointf = {
				type: "Feature",
				geometry: {
					type: "Point",
					coordinates: [lon, lat],
				},
			};
			for (let i = 0; i < geoData.data.features.length; i++) {
				try {
					if (booleanPointInPolygon(pointf, geoData.data.features[i])) {
						feature = geoData.data.features[i];
						break;
					}
				} catch (err) {
					console.log(err);
				}
			}
		}
		return feature;
	};

	const selectRegion = (newRegion) => {
		if (!selectedRegion || !newRegion) {
			selectedRegion = newRegion;
		} else {
			const alreadySelected = newRegion === selectedRegion;

			selectedRegion = alreadySelected && !selectedPoint ? undefined : newRegion;
		}

		if (selectedRegion && !selectedRegion.name) selectedRegion.name = selectedRegion[geoData.nameKey];

		stopSearch(selectedRegion ? selectedRegion.label : false);
	};

	const clearPoint = () => {
		selectedPoint = null;
		selectedRegion = null;
		hoveredRegion = null;
		tooltip = undefined;
		removeMarker();
		removePopup();
		map.selectChoropleth(geoData.layerId, false, true);
		selectRegion(false);
	};

	// feature is optional
	const selectPoint = (lngLat, optionalFeature, stayWhereYouAre) => {
		let feature; // = selectedRegion;
		let featureProperties;
		if (lngLat) {
			selectedPoint = lngLat;
			// addMarker(lngLat.lng, lngLat.lat);
		}
		if (optionalFeature) {
			feature = optionalFeature;
		} else if (lngLat) {
			feature = findFeature(lngLat.lng, lngLat.lat);
		} else if (selectedPoint) {
			feature = findFeature(selectedPoint.lng, selectedPoint.lat);
		}
		featureProperties = feature ? feature.properties : undefined;
		if (feature) {
			// D: hier habe ich das false eingefügt - man möchte via adresssuche auch auf kieze kommen die gerade gefiltert sind
			if (false && featureProperties.filtered === true) {
				clearPoint();
				map.toInitialView();
			} else if (featureProperties !== selectedRegion) {
				addPopup(feature);
				selectRegion(featureProperties);
				map.selectChoropleth(geoData.layerId, null, true);
				map.selectChoropleth(geoData.layerId, featureProperties.id, stayWhereYouAre);
			}
		} else {
			clearPoint();
		}
	};

	const scrollToTopOfMap = () => {
		scrollTo(elemMap, -90);
	};

	const clearSelection = () => {
		clearPoint();
		scrollToTopOfMap();
	};

	// ///////////////////////////////////////////////////////////
	// markers
	// ///////////////////////////////////////////////////////////

	const berlinMarkers = [];

	function showBerlinMarkers() {
		map.activated
			.then(() => map.loaded)
			.then(() => {
				markerData.forEach((d) => {
					const melc = document.createElement("div");
					const mel = document.createElement("div");
					melc.appendChild(mel);
					mel.classList.add("marker");
					mel.classList.add(`marker--${d.n}`);
					mel.innerHTML = d.label || "";
					berlinMarkers.push(new window.mapboxgl.Marker(melc).setLngLat([d.lon, d.lat]).addTo(map.map));
				});
			});
	}

	function hideBerlinMarkers() {
		map.activated
			.then(() => map.loaded)
			.then(() => {
				berlinMarkers.forEach((m) => {
					m.remove();
				});
			});
	}

	let ringshown = false;
	let ringshownonce = false;
	let ringmarker;
	function showRing() {
		if (ringshown) return;
		ringshown = true;
		if (!ringshownonce) {
			ringshownonce = true;
			map.activated
				.then(() => map.loaded)
				.then(() => {
					map.map.addSource("sbahnring", {
						type: "geojson",
						data: geoRing,
					});
					map.map.addLayer({
						id: "sbahnring1",
						type: "line",
						source: "sbahnring",
						layout: {},
						paint: {
							"line-color": "#ffffff",
							"line-width": 3,
						},
					});
					map.map.addLayer({
						id: "sbahnring2",
						type: "line",
						source: "sbahnring",
						layout: {},
						paint: {
							"line-color": "#1f1f1f",
							"line-width": 2,
							"line-dasharray": [1, 1],
						},
					});
					const pos = [13.277214090669595, 52.52379218491869];
					const el = document.createElement("div");
					el.className = "ring-marker";
					el.innerHTML = '<div class="ring-marker-text">S-Bahn-Ring</div>';
					ringmarker = new window.mapboxgl.Marker(el).setLngLat(pos);
					ringmarker.addTo(map.map);
				});
		} else {
			map.map.setLayoutProperty("sbahnring1", "visibility", "visible");
			map.map.setLayoutProperty("sbahnring2", "visibility", "visible");
			ringmarker.addTo(map.map);
		}
	}

	function hideRing() {
		if (!ringshown) return;
		map.map.setLayoutProperty("sbahnring1", "visibility", "none");
		map.map.setLayoutProperty("sbahnring2", "visibility", "none");
		ringmarker.remove();
		ringshown = false;
	}

	// ///////////////////////////////////////////////////////////
	// tooltip
	// ///////////////////////////////////////////////////////////

	$: tooltipRegion = hoveredRegion || selectedRegion || dataBerlin;

	const closeClick = () => clearPoint();

	function tooltipClick() {
		// clearPoint();
	}

	function tooltipMouseover() {
		if (window.innerWidth <= 544) return;
		tooltipHovered = true;
		if (selectedRegion) {
			hoveredRegion = undefined;
			removePopup();
			if (selectedRegion) addPopup(selectedRegion);
		}
	}

	function tooltipMouseout() {
		if (window.innerWidth <= 544) return;
		tooltipHovered = false;
	}

	let breakpoint;
	let platform;
	let orientation;
</script>

<MediaQuery bind:breakpoint bind:platform bind:orientation />
<div class="volksentscheid-klima-map">
	<div class="headline">Finde deinen Stimmbezirk</div>
	<div class="ui-container-top">
		<div class="legend" class:invert-colors={showLuftbilder}>
			<div class="legend-items">
				{#each options.filter((o) => o.legendLabel) as v}
					<div class="legend-item">
						<div class="legend-symbol" style:background={v.color} />
						<div class="legend-label">{@html v.legendLabel}</div>
					</div>
				{/each}
			</div>
		</div>
		<div class="search">
			<Search
				type={["geo"]}
				icon="pin"
				geoSearchBounds={[initialBounds[1], initialBounds[0]]}
				dataSearchData={geoData.data}
				dataSearchKeys={[geoData.nameKey]}
				dataSearchGetLabel={(d) => d[geoData.nameKey]}
				placeholder="Adresse eingeben"
				clearCallback={() => clearPoint()}
				resultCallback={(d) => {
					addMarker(d.lon, d.lat);
					selectPoint({ lng: d.lon, lat: d.lat });
				}}
				bind:stopSearch
			/>
		</div>
	</div>
	<div
		class="map-container"
		class:tooltip-shown={tooltip}
		on:mouseleave={() => {
			hoveredRegion = undefined;
			removePopup();
			if (selectedRegion) addPopup(selectedRegion);
		}}
	>
		<div class="map" bind:this={elemMap} />

		<div
			class="button-luftbilder"
			class:hidden={mapZoom < luftbilderButtonZoom && !showLuftbilder}
			class:luftbilder={showLuftbilder}
			on:click={() => {
				showLuftbilder = !showLuftbilder;
			}}
		>
			<div class="button-luftbilder-image button-luftbilder-image-on" />
			<div class="button-luftbilder-image button-luftbilder-image-off" />
			<div class="button-luftbilder-text mobile">
				{showLuftbilder ? "Straßen-karte" : "Luft-bilder"}
			</div>
			<div class="button-luftbilder-text desktop">
				{showLuftbilder ? "Auf Straßenkarte umschalten" : "Auf Luftbilder umschalten"}
			</div>
		</div>
		<div class="map-loading" class:mapLoaded>
			<div class="ts-spinner">
				<div class="ts-double-bounce1" />
				<div class="ts-double-bounce2" />
			</div>
		</div>
		{#if tooltipRegion}
			{@const hoveredAndSelectedAreSame =
				hoveredRegion && selectedRegion ? hoveredRegion[geoData.idKey] === selectedRegion[geoData.idKey] : false}
			{@const tooltipRegionIsSelected = (!hoveredRegion || hoveredAndSelectedAreSame) && !!selectedRegion}
			<Tooltip
				region={tooltipRegion}
				isSelected={tooltipRegionIsSelected}
				on:click={tooltipClick}
				on:mouseover={tooltipMouseover}
				on:mouseout={tooltipMouseout}
				closeCbk={closeClick}
				animateFly={platform === "mobile"}
				isTotal={tooltipRegion === dataBerlin}
			/>
		{/if}
	</div>
	<div class="attribution">
		Daten:&nbsp;<a href="https://www.berlin.de/wahlen/" target="_blank">Landeswahlleiter Berlin</a>
		Kartenbasis:&nbsp;<a href="https://www.openmaptiles.org/" target="_blank">&copy;&nbsp;OpenMapTiles</a>
		<a href="https://www.openstreetmap.org/copyright" target="_blank">&copy;&nbsp;OpenStreetMap contributors</a>
		{#if showLuftbilder}
			| <a
				target="_blank"
				href="https://fbinter.stadt-berlin.de/fb/berlin/service.jsp?id=a_luftbild2022_true_rgbi@senstadt&amp;type=FEED"
				>Geoportal Berlin / Digitale farbige Orthophotos 2022 (DOP20RGBI)</a
			>
		{/if}
	</div>
</div>

<style lang="scss">
	.volksentscheid-klima-map {
		font-family: $ff-sans;
		font-size: $fs-base;
		line-height: $lh-base;
		color: $color;
		user-select: none;
	}

	.map-container {
		position: relative;
		margin-bottom: 0;
		transition: margin 0.3s ease;

		$tooltip-height: 362px;

		// @media (min-width: 310px) and (max-width: $breakpoint-mobile-l) {
		// 	margin-bottom: calc(#{$tooltip-height} - #{$tooltip-offset} + 2vh);
		// }

		// @media (max-width: 309px) {
		// 	margin-bottom: calc(#{$tooltip-height} - #{$tooltip-offset} + 4vh);
		// }
	}

	.attribution {
		font-size: 14px;
		color: #666;
		line-height: 1.5em;
		margin-top: 12px;
		text-align: right;

		a {
			text-decoration: none;
		}
	}

	// UI
	.ui-container-top {
		display: flex;
		justify-content: space-between;
		margin-bottom: 5px;
		align-items: center;

		@media (max-width: $breakpoint-tablet-l) {
			flex-direction: column-reverse;
			margin-bottom: 5px;

			.search {
				width: 100%;
				margin-bottom: 8px;
			}
		}

		@media (min-width: #{$breakpoint-tablet-s + 1px}) and (max-width: $breakpoint-tablet-l) {
			align-items: center;

			.search {
				width: 635px;
			}
		}

		@media (min-width: #{$breakpoint-tablet-l + 1px}) {
			.search {
				flex-basis: 400px;
			}
		}
	}

	.ui-container-bottom {
		display: flex;
		justify-content: space-between;
	}

	.headline {
		font-weight: $bold;
		font-size: 16px;
		text-align: center;
		margin-bottom: 10px;

		@media (min-width: #{$breakpoint-tablet-l + 1px}) {
			display: none;
		}
	}

	// MAP
	.map {
		position: relative;
		height: 650px;
	}

	// desktop
	@media (min-width: #{$breakpoint-tablet-l + 1px}) {
	}

	// large tablets
	@media (min-width: #{$breakpoint-tablet-s + 1px}) and (max-width: $breakpoint-tablet-l) {
		.map {
			height: 600px;
		}
	}

	// small tablets
	@media (min-width: #{$breakpoint-mobile-l + 1px}) and (max-width: $breakpoint-tablet-s) {
		.map {
			height: 600px;
		}
	}

	// large/normal phones
	@media (min-width: #{$breakpoint-mobile-m + 1px}) and (max-width: $breakpoint-mobile-l) {
		.map {
			height: 460px;
		}
	}

	// small phones
	@media (min-width: #{$breakpoint-mobile-s + 1px}) and (max-width: $breakpoint-mobile-m) {
		.map {
			height: 420px;
		}
	}

	// very small phones
	@media (max-width: $breakpoint-mobile-s) {
		.map {
			height: 420px;
		}
	}

	:global(.mapboxgl-ctrl-scale) {
		bottom: -4px !important;
	}

	:global(.marker) {
		pointer-events: none;
		background-position: center bottom;
		background-size: contain;
		background-repeat: no-repeat;
		position: absolute;
		bottom: 0;
		left: 0;
		transform: translateX(-50%);
		font-weight: $bold;
		font-size: 11px;
		font-family: $ff-sans;
		line-height: 14px;
		color: #fff;
		text-shadow: 1px 1px #1f1f1f, -1px 1px #1f1f1f, -1px -1px #1f1f1f, 1px -1px #1f1f1f, 0 1px #1f1f1f, 0 -1px #1f1f1f,
			1px 0 #1f1f1f, -1px 0 #1f1f1f, 0 0 2px rgba(0, 0, 0, 0.1);
	}

	:global(.marker.marker--fernsehturm) {
		background-image: url(../assets/images/berlin/fernsehturm.svg);
		height: 45px;
		width: 20px;
	}

	:global(.ring-marker-text) {
		position: absolute;
		// left: -20px;
		// top: -20px;
		font-family: $ff-sans;
		white-space: nowrap;
		font-size: 11px;
		color: #272727;
		font-weight: $bold;
		text-shadow: 1px 1px #fff, -1px 1px #fff, 1px -1px #fff, -1px -1px #fff;
		transform: translate(-100%, -100%) !important;
		pointer-events: none;
	}

	:global(.region-popup) {
		pointer-events: none !important;
	}

	:global(.region-popup .mapboxgl-popup-content) {
		border-radius: 4px;
		font-family: $ff-sans;
		text-align: center;
		padding: 7px 9px !important;
		pointer-events: none !important;
	}

	:global(.region-popup .region) {
		font-weight: $bold;
		font-size: 9px;
		line-height: 13px;
		letter-spacing: 0.085em;
		text-transform: uppercase;
		color: #747474;
	}

	:global(.region-popup .name) {
		font-weight: $bold;
		letter-spacing: 0.015em;
		font-size: 13px;
		line-height: 14px;
	}

	:global(.region-popup .bezirk) {
		letter-spacing: 0.015em;
		font-size: 12px;
		line-height: 15px;
	}

	// very phones
	@media (max-width: $breakpoint-mobile-l) {
		:global(.marker) {
			font-size: 11px;
			line-height: 13px;
		}

		:global(.marker--fernsehturm) {
			height: 36px;
			width: 16px;
		}
	}

	// Anpassungen mapcontrols und scale
	// loading spinner

	.map {
		z-index: 0;
	}

	.map-loading {
		position: absolute;
		background: #f2f2f2;
		left: 0;
		top: 0;
		bottom: 0;
		right: 0;
		pointer-events: all;
		z-index: 1;
		transition: opacity 0.2s ease;
		opacity: 1;

		&.mapLoaded {
			opacity: 0;
			pointer-events: none;
		}
	}

	.ts-spinner {
		width: 40px;
		height: 40px;
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
	}

	.ts-double-bounce1,
	.ts-double-bounce2 {
		width: 100%;
		height: 100%;
		border-radius: 50%;
		background-color: #333;
		opacity: 0.6;
		position: absolute;
		top: 0;
		left: 0;
		animation: sk-bounce 2s infinite ease-in-out;
	}

	.ts-double-bounce2 {
		animation-delay: -1s;
	}

	@keyframes sk-bounce {
		0%,
		100% {
			transform: scale(0);
			-webkit-transform: scale(0);
		}

		50% {
			transform: scale(1);
			-webkit-transform: scale(1);
		}
	}

	// LEGENDE

	.legend {
		font-size: 13px;
		line-height: 20px;
		white-space: nowrap;
		display: flex;
		flex-direction: row;
		border-radius: 3px;
		text-shadow: -1px 1px 0 $white, 1px 1px 0 $white, 1px -1px 0 $white, -1px -1px 0 $white;
		// @media (min-width: #{$breakpoint-mobile-l + 1px}) {
		// 	&.invert-colors {
		// 		color: $white;
		// 		text-shadow: -1px 1px 0 $black, 1px 1px 0 $black, 1px -1px 0 $black, -1px -1px 0 $black;

		// 		.legend-symbol {
		// 			border: 1px solid $black;
		// 		}
		// 	}
		// }
	}

	.legend-title {
		font-weight: 600;
	}

	.legend-symbol {
		border-radius: 50%;
	}

	.legend-item {
		display: inline-block;

		&:last-child {
			margin-right: 0;
		}
	}

	@media (max-width: $breakpoint-mobile-l) {
		.legend-title {
			margin-right: 5px;
		}

		.legend-item {
			margin-right: 5px;
		}
	}

	@media (min-width: #{$breakpoint-mobile-l + 1px}) {
		.legend-title {
			margin-right: 15px;
		}

		.legend-item {
			margin-right: 10px;
		}
	}

	.legend-symbol {
		display: inline-block;
		width: 8px;
		height: 8px;
		border: 1px solid $white;
		// box-shadow: 0 0 2px rgba(0, 0, 0, 0.25);
		margin-right: 2px;
	}

	.legend-label {
		display: inline-block;
	}

	@media (max-width: $breakpoint-mobile-l) {
		.legend {
			position: relative;
			background: none;
			padding: 10px 0 10px 0;
			left: 0;
			top: 0;
			display: flex;
			justify-content: space-between;
			flex-wrap: wrap;
		}
	}

	// LUFTBILDER BUTTON

	.button-luftbilder {
		width: 48px;
		height: 48px;
		background: #fff;
		border-radius: 4px;
		box-shadow: 0 3px 5px rgba(0, 0, 0, 0.25);
		position: absolute;
		top: 20px;
		right: 20px;

		&.hidden {
			display: none;
		}
	}

	.button-luftbilder-image {
		position: absolute;
		left: 4px;
		top: 4px;
		width: calc(100% - 8px);
		height: calc(100% - 8px);
		border-radius: 3px;
		background-size: cover;
		background-position: center center;
		overflow: hidden;
	}

	.button-luftbilder-image-on {
		display: none;
		background-image: url(../assets/images/icons/icon_data_layer.jpg);
	}

	.button-luftbilder-image-off {
		background-image: url(../assets/images/icons/icon_satellite_layer.jpg);
		display: block;
	}

	.button-luftbilder.luftbilder {
		.button-luftbilder-image-on {
			display: block;
		}

		.button-luftbilder-image-off {
			display: none;
		}
	}

	.button-luftbilder-text {
		color: #6a6a6a;
		font-weight: 600;
		font-size: 11px;
		line-height: 12px;
		text-align: center;
		position: absolute;
		top: 100%;
		left: 50%;
		transform: translate(-50%, 5px);
		text-shadow: -1px 1px 0 #fff, 1px 1px 0 #fff, 1px -1px 0 #fff, -1px -1px 0 #fff;

		&.desktop {
			width: 84px;
		}
	}

	@media (min-width: #{$breakpoint-mobile-l + 1px}) {
		.button-luftbilder-text.mobile {
			display: none;
		}
	}

	@media (max-width: $breakpoint-mobile-l) {
		.button-luftbilder {
			width: 38px;
			height: 38px;
			top: 10px;
			right: 10px;
		}

		.button-luftbilder-image {
			left: 3px;
			top: 3px;
			width: calc(100% - 6px);
			height: calc(100% - 6px);
		}

		.button-luftbilder-text.desktop {
			display: none;
		}
	}
</style>
